/* global peopleDashboardUrl peopleDelayScenarioUrl peopleDelayScenarioExportUrl */

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import PeopleComposition from 'Components/people/PeopleComposition';
import { COUNT_MODE_HEADCOUNT, PEOPLE_CONTROLS_READY_EVENT } from 'Components/people/peopleControlStore';
import PeopleCostcentreRollup from 'Components/people/PeopleCostcentreRollup';
import PeopleDelayScenario from 'Components/people/PeopleDelayScenario';
import PeopleHeadcountRamp from 'Components/people/PeopleHeadcountRamp';
import PeopleKpiStrip from 'Components/people/PeopleKpiStrip';
import PeopleLevelStructure from 'Components/people/PeopleLevelStructure';
import PeopleMovement from 'Components/people/PeopleMovement';
import PeopleReconciliationStrip from 'Components/people/PeopleReconciliationStrip';
import PeopleRecruitmentOverview from 'Components/people/PeopleRecruitmentOverview';
import PeopleSpansAndLayers from 'Components/people/PeopleSpansAndLayers';
import { POSITION_TYPE_ORDER } from 'Components/people/positionTypes';

import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import SectionTile from 'CommonComponents/SectionTile';

/**
 * People → Dashboard sub-view.
 *
 * Owns no control state of its own: it subscribes to `window.peopleControls`
 * (published by the control rail, which sits outside every tabpanel so it
 * survives sub-view switches) and refetches on every change.
 */

const DEFAULT_CONTROLS = {
    count_mode: COUNT_MODE_HEADCOUNT,
    position_types: [...POSITION_TYPE_ORDER],
    compare_to_version_id: null,
};

/**
 * Query params for the dashboard payload.
 *
 * `position_types` is passed WITHOUT the `[]` suffix: axios appends `[]` itself
 * for array values, so `position_types` serialises to `position_types[]=P&…`
 * while `'position_types[]'` would double up into `position_types[][]=P`.
 *
 * `compare_to_version_id` is dropped rather than sent as null — axios stringifies
 * a null into the literal "null", which Spatie's always-on validation rejects with
 * a 422. Any boolean added here must be serialised as 1/0 for the same reason.
 */
function buildParams(controls) {
    const params = {
        position_types: controls.position_types,
        count_mode: controls.count_mode,
    };

    if (controls.compare_to_version_id != null) {
        params.compare_to_version_id = controls.compare_to_version_id;
    }

    return params;
}

/** Every tabpanel the mount sits inside, innermost first. */
function tabpanelChain() {
    const panels = [];
    let panel = document.getElementById('PeopleDashboard')?.closest('[role="tabpanel"]');

    while (panel) {
        panels.push(panel);
        panel = panel.parentElement?.closest('[role="tabpanel"]') ?? null;
    }

    return panels;
}

/**
 * Copy that names the scope's child level. Mode `department` is the event-level
 * Dashboard; every other mode keeps the department and cost-centre copy unchanged.
 */
function rollupCopy(mode) {
    if (mode === 'department') {
        return {
            header: 'Department roll-up',
            subHeader: 'Every department holding positions',
            labelHeader: 'Department',
            emptyMessage: 'No positions in this event yet.',
            movementSubHeader: 'Change against the selected compare-to version, by department',
        };
    }

    return {
        header: $t('Cost-centre roll-up'),
        subHeader: $t('Every cost centre holding positions, plus the department’s own'),
        labelHeader: undefined,
        emptyMessage: undefined,
        movementSubHeader: $t('Change against the selected compare-to version, by child level'),
    };
}

export default function PeopleDashboard({ url, delayScenarioUrl, delayScenarioExportUrl, deferUntilVisible = false }) {
    const [controls, setControls] = useState(() => window.peopleControls?.getState() ?? DEFAULT_CONTROLS);
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [visible, setVisible] = useState(!deferUntilVisible);

    // Subscribe to the rail. The rail is bundled in app.js and normally creates
    // the store before this component mounts, but the ready event covers the
    // reverse order too.
    useEffect(() => {
        let unsubscribe = null;

        function attach() {
            if (!window.peopleControls) {
                return;
            }

            setControls(window.peopleControls.getState());
            unsubscribe = window.peopleControls.subscribe(setControls);
        }

        attach();

        if (!unsubscribe) {
            document.addEventListener(PEOPLE_CONTROLS_READY_EVENT, attach, { once: true });
        }

        return () => {
            document.removeEventListener(PEOPLE_CONTROLS_READY_EVENT, attach);
            unsubscribe?.();
        };
    }, []);

    /*
     * ApexCharts sizes itself when it draws. When Dashboard is not the remembered
     * sub-view its tabpanel starts on Flowbite's bare `hidden` class, so the chart
     * draws into a zero-width container — and it never recovers on its own, because
     * it watches the window rather than its container. Nudge it once the panel is
     * shown.
     */
    useEffect(() => {
        // Every ancestor panel, not only the nearest: on Total Expenditure the
        // Dashboard sits inside the Workforce tab's own panel, which hides too.
        const panels = tabpanelChain();

        // Mounted outside any tab panel, it is on screen as soon as it renders.
        if (panels.length === 0) {
            setVisible(true);

            return undefined;
        }

        const isShown = () => panels.every((panel) => !panel.classList.contains('hidden'));
        let wasHidden = !isShown();

        const observer = new MutationObserver(() => {
            const hidden = !isShown();

            if (wasHidden && !hidden) {
                window.dispatchEvent(new Event('resize'));
                setVisible(true);
            }

            wasHidden = hidden;
        });

        panels.forEach((panel) => observer.observe(panel, { attributes: true, attributeFilter: ['class'] }));

        // Flowbite applies `hidden` to inactive panels on window load, so before
        // then every panel reads as shown.
        function checkOnceTabsInitialised() {
            wasHidden = !isShown();

            if (!wasHidden) {
                setVisible(true);
            }
        }

        if (document.readyState === 'complete') {
            checkOnceTabsInitialised();
        } else {
            window.addEventListener('load', checkOnceTabsInitialised, { once: true });
        }

        return () => {
            observer.disconnect();
            window.removeEventListener('load', checkOnceTabsInitialised);
        };
    }, []);

    // Guards against an out-of-order response overwriting a newer one: rapid
    // filter clicks fire overlapping requests and they do not resolve in order.
    const requestId = useRef(0);

    useEffect(() => {
        if (!visible) {
            return;
        }

        const id = ++requestId.current;

        setLoading(true);
        setFailed(false);

        axios
            .get(url, { params: buildParams(controls) })
            .then((response) => {
                if (id !== requestId.current) {
                    return;
                }

                setPayload(response.data);
                setLoading(false);
            })
            .catch(() => {
                if (id !== requestId.current) {
                    return;
                }

                setFailed(true);
                setLoading(false);
            });
    }, [url, controls, visible]);

    if (failed) {
        return <p className="tw-text-sm tw-text-red">{$t('The people dashboard could not be loaded. Please try again.')}</p>;
    }

    if (loading && !payload) {
        return (
            <div className="tw-flex tw-justify-center tw-py-10">
                <LoadingSpinner />
            </div>
        );
    }

    if (!payload) {
        return null;
    }

    // The roll-up's per-child ramp series and the top-level ramp buckets are the
    // same quarter grid, so the buckets' labels name the roll-up's periods too.
    const bucketLabels = (payload.ramp?.buckets ?? []).map((bucket) => bucket.label);
    const childMode = payload.ramp?.second_lens?.mode;
    const copy = rollupCopy(childMode);

    return (
        <div className={`tw-space-y-4 ${loading ? 'tw-opacity-60' : ''}`}>
            <PeopleKpiStrip kpiStrip={payload.kpi_strip} />

            <SectionTile header={$t('Headcount ramp')} subHeader={$t('Positions active in each period across the project life')}>
                <PeopleHeadcountRamp ramp={payload.ramp} countMode={controls.count_mode} />
            </SectionTile>

            <SectionTile header={$t('Composition at peak')}>
                <PeopleComposition composition={payload.composition_at_peak} />
            </SectionTile>

            {/* Absent at cost-centre level: Positions is that view, so a one-row
                roll-up of the thing being viewed would say nothing. */}
            {!!payload.rollup && (
                <SectionTile header={copy.header} subHeader={copy.subHeader}>
                    <PeopleCostcentreRollup
                        rollup={payload.rollup}
                        bucketLabels={bucketLabels}
                        labelHeader={copy.labelHeader}
                        emptyMessage={copy.emptyMessage}
                    />

                    {!!payload.reconciliation && (
                        <div className="tw-mt-5">
                            <PeopleReconciliationStrip reconciliation={payload.reconciliation} />
                        </div>
                    )}
                </SectionTile>
            )}

            {/* The strip renders at BOTH levels — a cost centre that does not
                foot is exactly what it exists to surface — so without a roll-up
                above it, it gets a tile of its own like every other panel. */}
            {!payload.rollup && !!payload.reconciliation && (
                <SectionTile header={$t('Reconciliation')} subHeader={$t('Whether this cost centre foots to its recorded workforce figure')}>
                    <PeopleReconciliationStrip reconciliation={payload.reconciliation} />
                </SectionTile>
            )}

            <SectionTile header={$t('Level structure')} subHeader={$t('Distribution over pay rates, highest band first')}>
                <PeopleLevelStructure levelStructure={payload.level_structure} />
            </SectionTile>

            <SectionTile header={$t('Spans and layers')} subHeader={$t('Measured from the reporting lines recorded on each position')}>
                <PeopleSpansAndLayers spansAndLayers={payload.spans_and_layers} />
            </SectionTile>

            <SectionTile header={$t('Movement')} subHeader={copy.movementSubHeader}>
                <PeopleMovement movement={payload.movement} mode={childMode} scopeMissing={!!payload.movement_scope_missing} />
            </SectionTile>

            <SectionTile header={$t('Recruitment overview')}>
                <PeopleRecruitmentOverview recruitment={payload.recruitment} />
            </SectionTile>

            {/* IB-925 — the delay scenario fetches its own payload: its criteria
                (mode, unit, delay, target, cut-off date) are its own state and
                change far more often than the Dashboard's, so folding it into this
                component's request would refetch every panel on every keystroke.
                It shares the rail's type filter, which is why the controls are
                passed down rather than re-subscribed to. */}
            {!!delayScenarioUrl && (
                <SectionTile header={$t('Delay scenario')} subHeader={$t('What moving uncommitted starts back would save — nothing here changes the budget')}>
                    <PeopleDelayScenario url={delayScenarioUrl} exportUrl={delayScenarioExportUrl} controls={controls} />
                </SectionTile>
            )}
        </div>
    );
}

const dashboardEl = document.getElementById('PeopleDashboard');

if (dashboardEl) {
    ReactDOM.render(
        <App>
            <PeopleDashboard
                url={peopleDashboardUrl}
                delayScenarioUrl={typeof peopleDelayScenarioUrl !== 'undefined' ? peopleDelayScenarioUrl : null}
                delayScenarioExportUrl={typeof peopleDelayScenarioExportUrl !== 'undefined' ? peopleDelayScenarioExportUrl : null}
                deferUntilVisible={dashboardEl.dataset.deferUntilVisible === 'true'}
            />
        </App>,
        dashboardEl,
    );
}
